/**
 * Modal para guardar un fragmento capturado del PDF
 */
import React, { useState, useEffect } from 'react';
import { X, Tag, FileText } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

interface SaveSnippetModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: { title: string; notes: string; tags: string[] }) => void;
  previewImage?: string; // Base64 de la imagen capturada
  documentName: string;
  pageNumber: number;
  // Para modo edición
  initialTitle?: string;
  initialNotes?: string;
  initialTags?: string[];
  snippetId?: string; // ID del snippet a editar
}

const SaveSnippetModal: React.FC<SaveSnippetModalProps> = ({
  isOpen,
  onClose,
  onSave,
  previewImage,
  documentName,
  pageNumber,
  initialTitle,
  initialNotes,
  initialTags,
  snippetId,
}) => {
  const { theme } = useTheme();
  const [title, setTitle] = useState('');
  const [notes, setNotes] = useState('');
  const [tagInput, setTagInput] = useState('');
  const [tags, setTags] = useState<string[]>([]);

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      // Si hay valores iniciales (modo edición), usarlos; si no, valores por defecto
      setTitle(initialTitle || `${documentName} - Pág. ${pageNumber}`);
      setNotes(initialNotes || '');
      setTagInput('');
      setTags(initialTags || []);
    }
  }, [isOpen, documentName, pageNumber, initialTitle, initialNotes, initialTags]);

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !tags.includes(tag)) {
      setTags([...tags, tag]);
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter((t) => t !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (title.trim()) {
      onSave({
        title: title.trim(),
        notes: notes.trim(),
        tags,
      });
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal Content */}
      <div
        className={`relative w-full max-w-2xl m-4 ${theme.colors.bg.primary} border ${theme.colors.border.primary} rounded-lg shadow-2xl`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between px-6 py-4 border-b ${theme.colors.border.primary}`}
        >
          <h2 className={`text-lg font-semibold ${theme.colors.text.primary}`}>
            {snippetId ? '✏️ Editar Fragmento PDF' : '💾 Guardar Fragmento PDF'}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded transition-colors`}
            title="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {/* Preview Image */}
            {previewImage && (
              <div className="mb-4">
                <label className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}>
                  Vista previa:
                </label>
                <div
                  className={`border-2 border-dashed ${theme.colors.border.primary} rounded-lg p-2 ${theme.colors.bg.primary}`}
                >
                  <img src={previewImage} alt="Preview" className="max-h-48 mx-auto rounded" />
                </div>
              </div>
            )}

            {/* Title */}
            <div>
              <label
                htmlFor="save-snippet-title"
                className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}
              >
                <FileText className="w-4 h-4 inline mr-2" />
                Título *
              </label>
              <input
                type="text"
                id="save-snippet-title"
                name="save-snippet-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Ej: EBITDA Q1 2025"
                required
                className={`w-full px-3 py-2 ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
              />
            </div>

            {/* Notes */}
            <div>
              <label
                htmlFor="save-snippet-notes"
                className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}
              >
                📝 Notas (opcional)
              </label>
              <textarea
                id="save-snippet-notes"
                name="save-snippet-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Agrega notas o contexto sobre este fragmento..."
                rows={3}
                className={`w-full px-3 py-2 ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none`}
              />
            </div>

            {/* Tags */}
            <div>
              <label
                htmlFor="save-snippet-tags"
                className={`block text-sm font-medium ${theme.colors.text.secondary} mb-2`}
              >
                <Tag className="w-4 h-4 inline mr-2" />
                Etiquetas (opcional)
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  id="save-snippet-tags"
                  name="save-snippet-tags"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Ej: finanzas, importante"
                  className={`flex-1 px-3 py-2 ${theme.colors.bg.input} ${theme.colors.text.primary} border ${theme.colors.border.primary} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                />
                <button
                  type="button"
                  onClick={handleAddTag}
                  className={`px-4 py-2 ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} rounded-lg hover:${theme.colors.bg.tertiary} transition-colors`}
                >
                  Agregar
                </button>
              </div>

              {/* Tags list */}
              {tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag) => (
                    <span
                      key={tag}
                      className={`inline-flex items-center gap-1 px-3 py-1 ${theme.colors.bg.secondary} text-nb-accent rounded-full text-sm`}
                    >
                      #{tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className={`hover:${theme.colors.text.primary}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Document info */}
            <div
              className={`p-3 ${theme.colors.bg.secondary} rounded-lg text-sm ${theme.colors.text.secondary}`}
            >
              <p>
                <strong>Documento:</strong> {documentName}
              </p>
              <p>
                <strong>Página:</strong> {pageNumber}
              </p>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className={`px-4 py-2 ${theme.colors.text.secondary} hover:${theme.colors.text.primary} ${theme.colors.state.hover} rounded-lg transition-colors`}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!title.trim()}
              className={`px-4 py-2 ${theme.colors.bg.tertiary} ${theme.colors.text.inverse} rounded-lg hover:${theme.colors.bg.tertiary} transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {snippetId ? '💾 Guardar Cambios' : '💾 Guardar Fragmento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveSnippetModal;
